import React, { useCallback, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FileDown, Loader2 } from "lucide-react";

type Props = {
  targetRef: React.RefObject<HTMLElement>;
  filename?: string;
  marginMm?: number;
  scale?: number;
  className?: string;
  label?: string;
  title?: string;
  forceWhiteBg?: boolean;

  avoidBreakSelector?: string;
  breakPaddingPx?: number;
};

type AvoidRange = { top: number; bottom: number };

export default function ExportTrainingPlanPdfButton({
  targetRef,
  filename = "training-plan",
  marginMm = 10,
  scale = 2,
  className = "",
  label = "PDF",
  title = "Exporter en PDF",
  forceWhiteBg = true,
  avoidBreakSelector = ".pdf-avoid-break",
  breakPaddingPx = 12,
}: Props) {
  const [exporting, setExporting] = useState(false);

  const exportPdf = useCallback(async () => {
    const node = targetRef.current;
    if (!node) {
      alert(
        "Zone à exporter introuvable (ref null). Place ref={pdfRef} sur un conteneur rendu."
      );
      return;
    }
    if (exporting) return;

    setExporting(true);

    try {
      // --- 0) Mesure DOM robuste (avec scrollHeight) ---
      const nodeRect = node.getBoundingClientRect();
      const domContentHeight = Math.max(node.scrollHeight, Math.ceil(nodeRect.height));

      // on récupère les éléments à éviter
      const avoidEls = Array.from(node.querySelectorAll(avoidBreakSelector)) as HTMLElement[];

      // Calcul top/bottom RELATIFS au node, en tenant compte du scroll interne
      // top = (elRect.top - nodeRect.top) + node.scrollTop
      const domRanges = avoidEls
        .map((el) => {
          const r = el.getBoundingClientRect();
          const top = Math.max(0, Math.floor(r.top - nodeRect.top + node.scrollTop));
          const bottom = Math.min(domContentHeight, Math.ceil(r.bottom - nodeRect.top + node.scrollTop));
          return { top, bottom };
        })
        .filter((r) => r.bottom > r.top)
        .sort((a, b) => a.top - b.top);

      // --- 1) Capture (html2canvas) ---
      const canvas = await html2canvas(node as HTMLElement, {
        backgroundColor: forceWhiteBg ? "#ffffff" : null,
        scale,
        useCORS: true,
        logging: false,
        onclone: (doc) => {
          doc.documentElement.classList.remove("dark");
          doc.body.classList.remove("dark");
          (doc.querySelectorAll(".dark") as unknown as HTMLElement[]).forEach((el) =>
            el.classList.remove("dark")
          );
          doc.documentElement.setAttribute("data-theme", "light");
          doc.body.setAttribute("data-theme", "light");

          const style = doc.createElement("style");
          style.textContent = `
            :root { color-scheme: light !important; }
            html, body { background: #fff !important; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          `;
          doc.head.appendChild(style);
        },
      });

      // --- 2) Prépare PDF A4 ---
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const margin = Math.max(0, marginMm);
      const contentW = pageW - margin * 2;
      const contentH = pageH - margin * 2;

      const srcW = canvas.width;
      const srcH = canvas.height;

      // px -> mm (fit largeur)
      const mmPerPx = contentW / srcW;
      const pxPerPage = Math.floor(contentH / mmPerPx);

      // --- 3) Convertit domRanges -> canvasRanges via scrollHeight ---
      const canvasPerDomPxY = srcH / domContentHeight;

      const avoidRanges: AvoidRange[] = domRanges
        .map((r) => ({
          top: Math.max(0, Math.floor(r.top * canvasPerDomPxY)),
          bottom: Math.min(srcH, Math.ceil(r.bottom * canvasPerDomPxY)),
        }))
        .filter((r) => r.bottom > r.top)
        .sort((a, b) => a.top - b.top);

      const adjustSliceEnd = (startY: number, proposedEndY: number) => {
        // si aucune zone -> rien à faire
        if (avoidRanges.length === 0) return proposedEndY;

        for (const r of avoidRanges) {
          // la coupe tombe dans un bloc ?
          if (proposedEndY > r.top && proposedEndY < r.bottom) {
            // on préfère couper AVANT le bloc
            const before = Math.max(startY + 1, r.top - breakPaddingPx);

            // minimum de remplissage page (sinon pages mini)
            const minFill = Math.floor(pxPerPage * 0.35);
            if (before - startY >= minFill) return before;

            // sinon, tenter de couper APRES le bloc (si ça tient)
            const after = Math.min(startY + pxPerPage, r.bottom + breakPaddingPx);
            if (after - startY <= pxPerPage && after > startY + 1) return after;

            // sinon on n’a pas le choix : on laisse proposedEndY
            return proposedEndY;
          }
        }
        return proposedEndY;
      };

      // --- 4) Découpe page par page ---
      let offsetY = 0;
      let pageIndex = 0;

      while (offsetY < srcH) {
        let endY = Math.min(offsetY + pxPerPage, srcH);
        endY = adjustSliceEnd(offsetY, endY);

        // sécurité anti-boucle
        if (endY <= offsetY + 1) {
          endY = Math.min(offsetY + pxPerPage, srcH);
        }

        const sliceH = endY - offsetY;

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = srcW;
        pageCanvas.height = sliceH;

        const ctx = pageCanvas.getContext("2d");
        if (!ctx) break;

        if (forceWhiteBg) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        }

        ctx.drawImage(canvas, 0, -offsetY);

        const imgData = pageCanvas.toDataURL("image/jpeg", 0.92);

        if (pageIndex > 0) pdf.addPage();

        const renderH = sliceH * mmPerPx;
        pdf.addImage(imgData, "JPEG", margin, margin, contentW, renderH);

        offsetY += sliceH;
        pageIndex += 1;
      }

      const safe =
        filename.replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 80) || "training-plan";
      pdf.save(`${safe}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l’export PDF.");
    } finally {
      setExporting(false);
    }
  }, [
    exporting,
    targetRef,
    filename,
    marginMm,
    scale,
    forceWhiteBg,
    avoidBreakSelector,
    breakPaddingPx,
  ]);

  return (
    <button
      onClick={exportPdf}
      disabled={exporting}
      className={
        className ||
        "inline-flex items-center gap-2 text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      }
      title={title}
      aria-label={title}
      type="button"
    >
      {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
      <span>{exporting ? "Export..." : label}</span>
    </button>
  );
}